// app_client/src/pages/admin/AccountManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAccountStore from '../../store/useAccountStore';
import useAuthStore from '../../store/authStore';
import useLocationStore from '../../store/useLocationStore';
import { 
  UserPlus, Edit, Search, Star, Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Pagination from '../../components/common/Pagination';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';
import { getAccountTypeLabel } from '../../utils/accountLabels';

const getEntityId = (entity) => {
  if (!entity) return '';
  if (typeof entity === 'object') return String(entity._id || entity.id || '');
  return String(entity);
};

const getAssignedToLabel = (assignedTo) => {
  if (!assignedTo) return '';
  if (typeof assignedTo === 'string') return assignedTo;

  const directName = [assignedTo?.name, assignedTo?.surname].filter(Boolean).join(' ').trim();
  if (directName) return directName;

  if (assignedTo?.fullName) return assignedTo.fullName;
  if (assignedTo?.username) return assignedTo.username;
  if (assignedTo?.email) return assignedTo.email;

  return '';
};

const getLocationEntityId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const getLocationEntityLabel = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return '';

  return (
    value.name ||
    value.title ||
    value.label ||
    [value.countryName, value.cityName].filter(Boolean).join(' ').trim() ||
    ''
  );
};

const transportModeLabels = {
  None: '-',
  Air: 'Hava',
  Sea: 'Deniz',
  Land: 'Kara'
};

const serviceTypeLabels = {
  None: '-',
  Delivery: 'Teslimat',
  International_Moving: 'Uluslararası Taşımacılık',
  Origin_Service: 'Çıkış Hizmeti',
  Domestic_Moving: 'Yurtiçi Evden Eve Taşımacılık',
  Domestic_Transportation: 'Yurtiçi Nakliye',
  Storage: 'Depolama'
};

const getDisplayLabel = (labels, value) => labels[String(value || 'None').trim()] || value || '-';

const includesEntityId = (list = [], id = '') => (
  Array.isArray(list) && list.some((item) => getEntityId(item) === id)
);

const accountTypeTabs = [
  { id: 'all', label: 'Tümü' },
  { id: 'PARTNER', label: 'Firma' },
  { id: 'CUSTOMER', label: 'Müşteri' },
  { id: 'TEKLIF', label: 'Teklif' }
];

const AccountSkeleton = () => (
  <div className="p-6 max-w-7xl mx-auto space-y-6">
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 bg-slate-50/50 p-4 rounded-4xl border border-slate-100">
      <div className="space-y-3">
        <div className="h-7 w-48 bg-slate-200 rounded-xl animate-pulse" />
        <div className="h-4 w-64 bg-slate-100 rounded-lg animate-pulse" />
      </div>
      <div className="flex gap-3">
        <div className="h-12 w-72 bg-white rounded-2xl shadow-sm animate-pulse" />
        <div className="h-12 w-32 bg-slate-200 rounded-2xl animate-pulse" />
      </div>
    </div>

    <div className="h-11 w-80 bg-slate-100 rounded-2xl border border-slate-200 animate-pulse" />

    <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
      {[1, 2, 3, 4, 5].map((item) => (
        <div key={item} className="grid grid-cols-[80px_1fr_180px_180px] gap-4 p-6 border-b border-slate-50 last:border-b-0">
          <div className="h-10 w-10 rounded-2xl bg-slate-100 animate-pulse mx-auto" />
          <div className="space-y-2">
            <div className="h-4 w-56 bg-slate-200 rounded-lg animate-pulse" />
            <div className="h-3 w-28 bg-slate-100 rounded-lg animate-pulse" />
          </div>
          <div className="h-4 w-24 bg-slate-100 rounded-lg animate-pulse" />
          <div className="flex justify-end gap-2">
            <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AccountManagement = () => {
  const navigate = useNavigate();
  const { accounts, pagination, fetchAccounts, loading, toggleWatch, deleteAccount } = useAccountStore();
  const {
    countries,
    allCities,
    fetchCountries,
    fetchAllCities
  } = useLocationStore();
  const currentUser = useAuthStore((state) => state.user);
  const currentUserId = getEntityId(currentUser?._id || currentUser?.id || currentUser?.userId);
  const { confirm } = useConfirmation();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [viewType, setViewType] = useState('all');
  const [accountType, setAccountType] = useState('all');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const didInitialFetch = useRef(false);
  const safeAccounts = Array.isArray(accounts) ? accounts : [];
  const countryLabelMap = useMemo(() => (
    new Map(
      (Array.isArray(countries) ? countries : [])
        .filter((country) => country?._id)
        .map((country) => [String(country._id), country.name || ''])
    )
  ), [countries]);
  const cityLabelMap = useMemo(() => (
    new Map(
      (Array.isArray(allCities) ? allCities : [])
        .filter((city) => city?._id)
        .map((city) => [String(city._id), city.name || ''])
    )
  ), [allCities]);

  // İlk mount'ta tek seferlik, kontrollü veri yükleme.
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;

    fetchCountries();
    fetchAllCities();
    fetchAccounts({ viewType, type: accountType === 'all' ? undefined : accountType, page: 1 }).finally(() => {
      setHasLoadedOnce(true);
    });
  }, [accountType, viewType, fetchAccounts, fetchCountries, fetchAllCities]);

  useEffect(() => {
    if (!hasLoadedOnce) return undefined;

    const delayDebounceFn = setTimeout(() => {
      fetchAccounts({ search: searchTerm, viewType, type: accountType === 'all' ? undefined : accountType, page: 1 });
    }, 350);

    return () => clearTimeout(delayDebounceFn);
  }, [accountType, searchTerm, viewType, hasLoadedOnce, fetchAccounts]);

  const handlePageChange = (newPage) => {
    fetchAccounts({ search: searchTerm, viewType, type: accountType === 'all' ? undefined : accountType, page: newPage });
  };

  const handleWatchClick = async (e, id) => {
    e.stopPropagation();
    const result = await toggleWatch(id);
    if (result.success) {
      toast.success("İzleme listesi güncellendi");
    } else {
      toast.error(result.message || "İzleme durumu güncellenemedi");
    }
  };

  const handleDeleteAccount = async (e, account) => {
    e.stopPropagation();
    if (await confirm(`${account.name} carisini silmek istediğinize emin misiniz?`)) {
      try {
        const res = await deleteAccount(account._id);
        if (res.success) {
          toast.success("Cari hesap silindi");
        } else {
          toast.error(res.message || "Cari hesap silinemedi", { duration: 6000 });
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Beklenmedik bir hata oluştu.";
        toast.error(errorMessage, { duration: 6000 });
        console.error("Detaylı Hata:", error);
      }
    }
  };

  const resolveLocationName = (value, fallbackMap) => {
    const directLabel = getLocationEntityLabel(value);
    if (directLabel) return directLabel;

    const entityId = getLocationEntityId(value);
    if (!entityId) return '-';

    return fallbackMap.get(entityId) || '-';
  };

  const renderLocationLine = (value, fallbackMap) => {
    const resolved = resolveLocationName(value, fallbackMap);
    return resolved === '-' ? (
      <span>-</span>
    ) : (
      <span className="truncate" title={resolved}>
        {resolved}
      </span>
    );
  };

  if (loading && !hasLoadedOnce) {
    return <AccountSkeleton />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header & Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 bg-slate-50/50 p-4 rounded-4xl border border-slate-100">
        <div className="shrink-0">
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Müşteri Yönetimi</h1>
          <p className="text-slate-500 text-sm">Sistemdeki tüm ticari hesaplar</p>
        </div>

        <div className="flex items-center gap-3 flex-1 md:justify-end">
          <div className="relative w-full max-w-xs group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500" size={16} />
            <input 
              type="text" 
              placeholder="Ara..."
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-transparent bg-white shadow-sm focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm font-bold"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button onClick={() => navigate('/accounts/new')} className="bg-slate-900 hover:bg-black text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
            <UserPlus size={18} className="lg:mr-2 inline" /> 
            <span className="hidden lg:inline">Yeni Müşteri</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 p-1 bg-slate-100 w-fit rounded-2xl border border-slate-200">
        {['all', 'my', 'tracked'].map(id => (
          <button
            key={id}
            onClick={() => setViewType(id)}
            className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${viewType === id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {id === 'all' ? 'Tümü' : id === 'my' ? 'Benimkiler' : 'Takip Ettiklerim'}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 p-1 bg-white w-fit rounded-2xl border border-slate-100 shadow-sm">
        {accountTypeTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setAccountType(tab.id)}
            className={`px-5 py-2 rounded-xl text-xs font-black transition-all ${accountType === tab.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden relative">
        {loading && <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center font-bold text-slate-400">Güncelleniyor...</div>}
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
            <tr className="border-b border-slate-50">
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-left w-[36%]">Cari Hesap</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-left w-[28%]">Lojistik Sınıflandırma</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-left w-[20%]">Lokasyon</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right w-[16%]">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {safeAccounts.map((acc) => {
                const isWatching = includesEntityId(acc.watchers, currentUserId);

                return (
                  <tr key={acc._id} className="hover:bg-slate-50/80 transition-all group cursor-pointer" onClick={() => navigate(`/accounts/${acc._id}`)}>
                    <td className="p-6 align-top">
                      <div className="flex flex-col">
                        <span className="font-black text-slate-800">{acc.name}</span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{acc.code} • {getAccountTypeLabel(acc.type || 'CUSTOMER')}</span>
                        {getAssignedToLabel(acc.assignedTo) ? (
                          <span className="inline-flex items-center gap-1 mt-1 rounded-md bg-slate-100 text-slate-700 px-2 py-0.5 text-[11px] font-medium border border-slate-200/80 w-fit">
                            <span className="font-black uppercase tracking-wide text-[10px] text-slate-500">Temsilci:</span>
                            <span className="truncate max-w-[220px]">{getAssignedToLabel(acc.assignedTo)}</span>
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-6 align-top">
                      <div className="flex flex-col gap-2">
                        <div className="font-black text-slate-800">{getDisplayLabel(transportModeLabels, acc.transportMode)}</div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            acc.transportMode && acc.transportMode !== 'None'
                              ? 'border-blue-100 bg-blue-50 text-blue-700'
                              : 'border-slate-200 bg-slate-50 text-slate-400'
                          }`}>
                            Sektör
                          </span>
                          <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            acc.serviceType && acc.serviceType !== 'None'
                              ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-slate-50 text-slate-400'
                          }`}>
                            Hizmet
                          </span>
                        </div>
                        <div className="text-[11px] font-medium text-slate-500">
                          <span className="mr-2 font-black uppercase tracking-widest text-[10px] text-slate-400">
                            Sektör:
                          </span>
                          {acc.transportMode && acc.transportMode !== 'None'
                            ? getDisplayLabel(transportModeLabels, acc.transportMode)
                            : '-'}
                        </div>
                        <div className="text-[11px] font-medium text-slate-500">
                          <span className="mr-2 font-black uppercase tracking-widest text-[10px] text-slate-400">
                            Hizmet:
                          </span>
                          {acc.serviceType && acc.serviceType !== 'None'
                            ? getDisplayLabel(serviceTypeLabels, acc.serviceType)
                            : '-'}
                        </div>
                      </div>
                    </td>
                    <td className="p-6 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="font-black text-slate-800">
                          {renderLocationLine(acc.country, countryLabelMap)}
                        </div>
                        <div className="text-[11px] font-medium text-slate-500">
                          {renderLocationLine(acc.city, cityLabelMap)}
                        </div>
                      </div>
                    </td>
                    <td className="p-6 align-top text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={(e) => handleWatchClick(e, acc._id)}
                          className={`p-2 rounded-xl transition-colors ${isWatching ? 'bg-amber-50 text-amber-400' : 'text-slate-300 hover:bg-slate-50'}`}
                          title={isWatching ? "İzlemeden çıkar" : "İzlemeye al"}
                        >
                          <Star
                            size={18}
                            className={isWatching ? "fill-amber-400" : ""}
                            fill={isWatching ? "currentColor" : "none"}
                          />
                        </button>
                        <button onClick={() => navigate(`/accounts/${acc._id}/edit`)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl"><Edit size={18} /></button>
                        <button onClick={(e) => handleDeleteAccount(e, acc)} className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl"><Trash2 size={18} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {safeAccounts.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-16 text-center text-slate-400 font-bold">
                    Cari hesap bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination 
          currentPage={pagination.currentPage} 
          totalPages={pagination.totalPages} 
          onPageChange={handlePageChange} 
        />
      </div>
    </div>
  );
};

export default AccountManagement;
